import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import { useTableSort } from '../hooks/useTableSort';
import SortableTh from '../components/SortableTh';
import {
    Eye,
    RefreshCw,
    ShoppingBag,
    X,
    Calendar,
    Truck,
    CreditCard,
    ExternalLink,
} from 'lucide-react';

const ESTADOS = [
    { id: 'pagada', label: 'Pagadas', description: 'Listas para despachar o retirar' },
    { id: 'pendiente_pago', label: 'Pendientes de pago', description: 'Esperando confirmación del pago' },
    { id: 'despachada', label: 'Despachadas', description: 'Entregadas al cliente' },
    { id: 'cancelada', label: 'Canceladas', description: 'Órdenes anuladas' },
];

const ESTADO_BADGE = {
    pagada: 'bg-emerald-100 text-emerald-800',
    pendiente_pago: 'bg-amber-100 text-amber-800',
    despachada: 'bg-blue-100 text-blue-800',
    cancelada: 'bg-red-100 text-red-800',
    reembolsada: 'bg-gray-100 text-gray-700',
};

function formatClp(amount) {
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
}

function formatDate(dateString) {
    return new Date(dateString).toLocaleString('es-CL');
}

function formatEstado(estado) {
    return (estado || '').replace(/_/g, ' ').toUpperCase();
}

function formatDireccion(direccion) {
    if (!direccion) return null;
    if (typeof direccion === 'string') return direccion;
    const calle = direccion.direccion || direccion.calle || '';
    const comuna = direccion.comuna || direccion.comuna_id || '';
    const parts = [calle, comuna].filter(Boolean);
    return parts.length ? parts.join(', ') : JSON.stringify(direccion);
}

function StatusBadge({ estado }) {
    const cls = ESTADO_BADGE[estado] || 'bg-gray-100 text-gray-700';
    return (
        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
            {formatEstado(estado)}
        </span>
    );
}

export default function EcomOrdersPage() {
    const [orders, setOrders] = useState([]);
    const [estado, setEstado] = useState('pagada');
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            const url = new URL(`${API_URL}/api/ecom-orders`);
            url.searchParams.set('estado', estado);
            if (q.trim()) url.searchParams.set('q', q.trim());

            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `HTTP ${res.status}`);
            }
            setOrders(await res.json());
        } catch (err) {
            setError(err.message);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [estado]);

    const openDetail = async (numero) => {
        try {
            const res = await fetch(`${API_URL}/api/ecom-orders/${numero}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!res.ok) throw new Error('No se pudo cargar la orden');
            setSelected(await res.json());
        } catch (err) {
            alert(err.message);
        }
    };

    const cancelOrder = async (id) => {
        if (!confirm('¿Cancelar esta orden? Si está pagada, se repondrá el stock en bodega ecommerce.')) {
            return;
        }
        try {
            const res = await fetch(`${API_URL}/api/ecom-orders/${id}/cancel`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || 'No se pudo cancelar');
            }
            setSelected(null);
            fetchOrders();
        } catch (err) {
            alert(err.message);
        }
    };

    const markDispatched = async (id) => {
        if (!confirm('¿Marcar esta orden como despachada?')) return;
        try {
            const res = await fetch(`${API_URL}/api/ecom-orders/${id}/dispatch`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || 'No se pudo marcar como despachada');
            }
            setSelected(null);
            fetchOrders();
        } catch (err) {
            alert(err.message);
        }
    };

    const currentEstadoMeta = ESTADOS.find((s) => s.id === estado);

    const { sorted: sortedOrders, thProps } = useTableSort(orders, {
        defaultKey: 'created_at',
        defaultDir: 'desc',
        getters: {
            venta_id: (o) => Number(o.venta_id) || 0,
            item_count: (o) => Number(o.item_count) || 0,
            total: (o) => Number(o.total) || 0,
        },
    });

    const { sorted: sortedOrderItems, thProps: orderItemsThProps } = useTableSort(selected?.items || [], {
        getters: {
            cantidad: (i) => Number(i.cantidad) || 0,
            precio_unitario: (i) => Number(i.precio_unitario) || 0,
            subtotal: (i) => Number(i.subtotal || i.precio_total) || 0,
        },
    });

    return (
        <div className="page p-6 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <ShoppingBag size={28} className="text-indigo-700" />
                        Ventas Online
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Órdenes del sitio web supercolchagua.cl — seguimiento y despacho
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <input
                        type="text"
                        placeholder="Buscar nº orden o email..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchOrders()}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[220px]"
                    />
                    <button
                        type="button"
                        onClick={fetchOrders}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-50"
                    >
                        <RefreshCw size={16} /> Actualizar
                    </button>
                    {!loading && (
                        <div className="bg-indigo-50 text-indigo-800 px-4 py-2 rounded-lg font-medium text-sm">
                            {orders.length} orden{orders.length !== 1 ? 'es' : ''}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex bg-gray-100 rounded-lg p-1 mb-6 flex-wrap gap-1">
                {ESTADOS.map((s) => (
                    <button
                        key={s.id}
                        type="button"
                        onClick={() => setEstado(s.id)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            estado === s.id
                                ? 'bg-white text-indigo-800 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            {currentEstadoMeta && (
                <p className="text-sm text-gray-500 mb-4 -mt-2">{currentEstadoMeta.description}</p>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-4 text-sm">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
                </div>
            ) : orders.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                    <div className="bg-gray-100 p-4 rounded-full inline-flex mb-4">
                        <ShoppingBag size={32} className="text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">
                        No hay órdenes en estado «{currentEstadoMeta?.label || estado}»
                    </h3>
                    <p className="text-gray-500 mt-1">
                        Las compras del sitio web aparecerán aquí cuando el cliente complete el checkout.
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wider text-gray-500 font-semibold">
                                    <SortableTh columnKey="numero_orden" {...thProps} className="p-4">N° Orden</SortableTh>
                                    <SortableTh columnKey="estado" {...thProps} className="p-4">Estado</SortableTh>
                                    <SortableTh columnKey="venta_id" {...thProps} className="p-4">Venta</SortableTh>
                                    <SortableTh columnKey="prv_code" {...thProps} className="p-4">PRV</SortableTh>
                                    <SortableTh columnKey="nombre_contacto" {...thProps} className="p-4">Cliente</SortableTh>
                                    <SortableTh columnKey="metodo_entrega" {...thProps} className="p-4">Entrega</SortableTh>
                                    <SortableTh columnKey="item_count" {...thProps} className="p-4">Items</SortableTh>
                                    <SortableTh columnKey="total" {...thProps} className="p-4 text-right" align="right">Total</SortableTh>
                                    <SortableTh columnKey="created_at" {...thProps} className="p-4">Fecha</SortableTh>
                                    <th className="p-4 text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sortedOrders.map((o) => (
                                    <tr
                                        key={o.id}
                                        className="hover:bg-gray-50 transition-colors group"
                                    >
                                        <td className="p-4 font-mono text-sm text-gray-900">
                                            {o.numero_orden}
                                        </td>
                                        <td className="p-4">
                                            <StatusBadge estado={o.estado} />
                                        </td>
                                        <td className="p-4">
                                            {o.venta_id ? (
                                                <Link
                                                    to={`/ventas?venta=${o.venta_id}`}
                                                    className="rounded bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-mono font-semibold hover:bg-blue-200"
                                                    title="Ver venta en historial"
                                                >
                                                    #{o.venta_id}
                                                </Link>
                                            ) : (
                                                <span className="text-gray-400 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            {o.prv_code ? (
                                                <span className="rounded bg-amber-100 text-amber-900 px-2 py-0.5 text-xs font-mono font-semibold">
                                                    {o.prv_code}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <p className="font-medium text-gray-800">{o.nombre_contacto}</p>
                                            <p className="text-xs text-gray-500">{o.email_contacto}</p>
                                        </td>
                                        <td className="p-4 capitalize text-gray-600 text-sm">
                                            {o.metodo_entrega}
                                        </td>
                                        <td className="p-4 text-gray-600">{o.item_count}</td>
                                        <td className="p-4 text-right font-bold text-gray-900">
                                            {formatClp(o.total)}
                                        </td>
                                        <td className="p-4 text-gray-500 text-sm">
                                            <span className="flex items-center gap-1">
                                                <Calendar size={14} />
                                                {formatDate(o.created_at)}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    type="button"
                                                    className="p-2 text-indigo-700 hover:bg-indigo-50 rounded-lg"
                                                    title="Ver detalle"
                                                    onClick={() => openDetail(o.numero_orden)}
                                                >
                                                    <Eye size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {selected &&
                ReactDOM.createPortal(
                    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
                            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-start bg-gray-50">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800 font-mono">
                                        {selected.numero_orden}
                                    </h2>
                                    <p className="text-sm text-gray-500 flex items-center gap-2 mt-1 flex-wrap">
                                        <StatusBadge estado={selected.estado} />
                                        <span>• {formatDate(selected.created_at)}</span>
                                        {selected.prv_code && (
                                            <span className="font-mono text-amber-800 bg-amber-100 px-2 py-0.5 rounded text-xs">
                                                {selected.prv_code}
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSelected(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto">
                                {selected.estado === 'pagada' && (
                                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
                                        <p className="text-sm text-indigo-900 font-medium">
                                            Pago confirmado — prepare el pedido para{' '}
                                            {selected.metodo_entrega === 'retiro'
                                                ? 'retiro en tienda'
                                                : 'despacho'}
                                            . No vuelva a cobrar al cliente.
                                        </p>
                                        <div className="flex flex-wrap gap-3 mt-2">
                                            {selected.venta_id && (
                                                <Link
                                                    to={`/ventas?venta=${selected.venta_id}`}
                                                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                                                >
                                                    Ver venta #{selected.venta_id} en historial
                                                    <ExternalLink size={12} />
                                                </Link>
                                            )}
                                            {selected.preventa_id && (
                                                <Link
                                                    to="/preventas"
                                                    className="inline-flex items-center gap-1 text-xs text-indigo-800 hover:underline"
                                                >
                                                    Ver preventa #{selected.preventa_id}
                                                    <ExternalLink size={12} />
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                                            Cliente
                                        </h3>
                                        <p className="font-medium text-gray-900">{selected.nombre_contacto}</p>
                                        <p className="text-sm text-gray-600">{selected.email_contacto}</p>
                                        <p className="text-sm text-gray-600">{selected.telefono_contacto}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
                                            <Truck size={14} /> Entrega
                                        </h3>
                                        <p className="capitalize font-medium text-gray-900">
                                            {selected.metodo_entrega}
                                        </p>
                                        {formatDireccion(selected.direccion_envio) && (
                                            <p className="text-sm text-gray-600 mt-1">
                                                {formatDireccion(selected.direccion_envio)}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {(selected.payment_provider || selected.payment_status) && (
                                    <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1">
                                            <CreditCard size={14} /> Pago
                                        </h3>
                                        <dl className="grid grid-cols-2 gap-3 text-sm">
                                            <div>
                                                <dt className="text-gray-500">Pasarela</dt>
                                                <dd className="font-medium">{selected.payment_provider || '—'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-gray-500">Estado pago</dt>
                                                <dd className="font-medium">{selected.payment_status || '—'}</dd>
                                            </div>
                                            {selected.payment_ref && (
                                                <div className="col-span-2">
                                                    <dt className="text-gray-500">Referencia</dt>
                                                    <dd className="font-mono text-xs break-all">
                                                        {selected.payment_ref}
                                                    </dd>
                                                </div>
                                            )}
                                        </dl>
                                    </div>
                                )}

                                <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">
                                        Resumen
                                    </h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                        <div>
                                            <p className="text-gray-500">Subtotal</p>
                                            <p className="font-semibold">{formatClp(selected.sub_total)}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Envío</p>
                                            <p className="font-semibold">{formatClp(selected.costo_envio)}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">IVA</p>
                                            <p className="font-semibold">{formatClp(selected.iva)}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Total</p>
                                            <p className="font-bold text-indigo-800 text-lg">
                                                {formatClp(selected.total)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <h3 className="text-lg font-bold text-gray-800 mb-3">Productos</h3>
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-50 text-gray-500 font-medium border-b">
                                            <tr>
                                                <SortableTh columnKey="producto_nombre" {...orderItemsThProps} className="px-4 py-3">Producto</SortableTh>
                                                <SortableTh columnKey="cantidad" {...orderItemsThProps} className="px-4 py-3 text-right" align="right">Cant.</SortableTh>
                                                <SortableTh columnKey="precio_unitario" {...orderItemsThProps} className="px-4 py-3 text-right" align="right">Unit.</SortableTh>
                                                <SortableTh columnKey="subtotal" {...orderItemsThProps} className="px-4 py-3 text-right" align="right">Subtotal</SortableTh>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {sortedOrderItems.map((i) => (
                                                <tr key={i.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">
                                                        <span className="font-medium text-gray-800">
                                                            {i.producto_nombre || `#${i.producto_id}`}
                                                        </span>
                                                        {i.codigo_barra && (
                                                            <span className="block text-xs text-gray-500 font-mono">
                                                                {i.codigo_barra}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">{i.cantidad}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        {formatClp(i.precio_unitario)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-semibold">
                                                        {formatClp(i.subtotal)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                                {selected.estado === 'pagada' && (
                                    <button
                                        type="button"
                                        className="px-4 py-2 bg-indigo-500 text-gray-900 rounded-lg hover:bg-indigo-600 font-medium flex items-center gap-2"
                                        onClick={() => markDispatched(selected.id)}
                                    >
                                        <Truck size={18} /> Marcar despachada
                                    </button>
                                )}
                                {selected.estado !== 'cancelada' &&
                                    selected.estado !== 'reembolsada' &&
                                    selected.estado !== 'despachada' && (
                                        <button
                                            type="button"
                                            className="px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 font-medium"
                                            onClick={() => cancelOrder(selected.id)}
                                        >
                                            Cancelar orden
                                        </button>
                                    )}
                                <button
                                    type="button"
                                    onClick={() => setSelected(null)}
                                    className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
